import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSelectedCategoryPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import ProductSearchScoreData from "@insite/content-library/Components/ProductSearchScoreData";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import ProductListActions from "@insite/content-library/Widgets/ProductList/ProductListActions";
import ProductListProductImage from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import ProductListProductInformation from "@insite/content-library/Widgets/ProductList/ProductListProductInformation";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomApplicationState from "@tLinc/Store/Data/CustomApplicationState/CustomApplicationState";
import React, { FC } from "react";
import { connect } from "react-redux";

import { css } from "styled-components";

const mapStateToProps = (state: CustomApplicationState) => {
    const location = getLocation(state);
    const pathnameLowerCase = location.pathname.toLowerCase();

    const selectedCategoryPath = getSelectedCategoryPath(state);

    const path =
        selectedCategoryPath ||
        (pathnameLowerCase.startsWith("/content/") || pathnameLowerCase.startsWith("/.spire/")
            ? ""
            : location.pathname);
    const catalogPage = getCatalogPageStateByPath(state, path).value;

    return {
        category: getCategoryState(state, catalogPage?.categoryIdWithBrandId ?? catalogPage?.categoryId).value,
        categoryWithHidePricing: state.LINCData.additionalSettingsReducer?.value?.categoryWithHidePricing,
    };
};

type Props = ReturnType<typeof mapStateToProps> & ProductCardSelections & HasProduct;

export interface ProductListProductCardStyles {
    gridContainer?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    actionsGridItem?: GridItemProps;
    searchScoreDataGridItem?: GridItemProps;
    sponsoredText?: TypographyPresentationProps;
    mainGridContainer?: InjectableCss;
    replacementMessageText?: TypographyPresentationProps;
}

export const productCardStyles: ProductListProductCardStyles = {
    gridContainer: {
        gap: 0,
        css: css`
            position: relative;
            padding: 30px 0;
            min-width: 320px; /* prevent overlap with second column */
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 15px 15px 0px 15px;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    leftColumnGridItem: {
        width: [4, 4, 4, 3, 3],
        css: css`
            height: 100%;
            flex-direction: column;
            padding-left: 5px;
        `,
    },
    rightColumnGridItem: {
        width: [8, 8, 8, 9, 9],
        css: css`
            padding-left: 15px;
            padding-right: 20px;
        `,
    },
    infoGridItem: {
        width: [12, 12, 12, 12, 12],
    },
    actionsGridItem: {
        width: [12, 12, 12, 5, 5],
        css: css`
            padding: 30px 0px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 0px;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    searchScoreDataGridItem: {
        width: 12,
    },
    sponsoredText: {
        css: css`
            margin-bottom: 5px;
            font-size: 0.9em;
            opacity: 0.5;
        `,
    },
    mainGridContainer: {
        css: css`
            display: flex;
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: column;
                        `,
                    ],
                    "max",
                )}
        `,
    },
};

const styles = productCardStyles;

const ProductListProductCard: FC<Props> = props => {
    const { categoryWithHidePricing, category } = props;
    const [replacementMessageText, setReplacementMessageText] = React.useState("");
    React.useEffect(() => {
        const replacementMessage = props.product.properties?.replacementMessage;
        const replacement: string[] = replacementMessage?.split(",");
        let tempReplacementMessageText: string;
        if (replacement?.length > 0) {
            tempReplacementMessageText = `Replacement of Item # ${replacement[0]}`;
            setReplacementMessageText(tempReplacementMessageText);
        }
    }, []);

    return (
        <StyledWrapper {...styles.mainGridContainer}>
            <GridContainer {...styles.gridContainer} data-test-selector={`productListProductCard${props.product.id}`}>
                <GridItem {...styles.leftColumnGridItem}>
                    {props.product.isSponsored && (
                        <Typography {...styles.sponsoredText}>{translate("Sponsored")}</Typography>
                    )}
                    {replacementMessageText && (
                        <Typography {...styles.replacementMessageText}>{replacementMessageText}</Typography>
                    )}

                    <ProductListProductImage
                        showImage={props.showImage}
                        showCompare={props.showCompare}
                        showImageBadge={props.showImageBadges}
                        showTextBadge={props.showTextBadges}
                    />
                </GridItem>
                <GridItem {...styles.rightColumnGridItem}>
                    <GridContainer {...styles.rightColumnGridContainer}>
                        <GridItem {...styles.infoGridItem}>
                            <ProductListProductInformation
                                showBrand={props.showBrand}
                                showTitle={props.showTitle}
                                showPartNumbers={props.showPartNumbers}
                                showAvailability={props.showAvailability}
                                showAttributes={props.showAttributes}
                                showImageBadges={props.showImageBadges}
                                maxImageBadges={props.maximumNumberOfImageBadges}
                                showTextBadges={props.showTextBadges}
                                maxTextBadges={props.maximumNumberOfTextBadges}
                            />
                        </GridItem>

                        <GridItem {...styles.searchScoreDataGridItem}>
                            <ProductSearchScoreData />
                        </GridItem>
                    </GridContainer>
                </GridItem>
            </GridContainer>
            <GridItem {...styles.actionsGridItem}>
                <ProductListActions showPrice={props.showPrice} showAddToList={props.showAddToList} />
            </GridItem>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(withProduct(ProductListProductCard));
